import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  DollarSign,
  Plug,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { periodLabel, type CommandCenter } from '@/services/marketingIntegrations.service';
import {
  channelCpaList,
  deriveFindings,
  formatMoney,
  formatNumber,
  topChannelsBySessions,
} from './derive';
import { useMetaAdsEnabled } from '@/pages/marketing/metaAds';
import { SEARCH_CONSOLE_VISIBLE } from './searchConsoleVisibility';

interface Props {
  cc: CommandCenter;
  targetCpa: number;
}

const TONE_STYLES: Record<'good' | 'warn' | 'bad', { card: string; icon: string; Icon: typeof CheckCircle2 }> = {
  good: { card: 'border-emerald-200 bg-emerald-50', icon: 'text-emerald-600', Icon: CheckCircle2 },
  warn: { card: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', Icon: AlertTriangle },
  bad:  { card: 'border-red-200 bg-red-50',     icon: 'text-red-600',     Icon: TrendingDown },
};

export default function OverviewTab({ cc, targetCpa }: Props) {
  const { enabled: metaAdsEnabled } = useMetaAdsEnabled();
  const ga = cc.dashboard.google_analytics;
  const gads = cc.dashboard.google_ads;
  const meta = cc.dashboard.meta_ads;

  const totalSpend = (gads?.configured ? gads.spend : 0) + (meta?.configured ? meta.spend : 0);
  const totalConv =
    (ga?.configured ? ga.conversions : 0) +
    (gads?.configured ? gads.conversions : 0) +
    (meta?.configured ? meta.conversions : 0);
  const blendedCpa = totalConv > 0 ? totalSpend / totalConv : 0;

  const channelCpa = channelCpaList(cc.dashboard);
  const bestChannel = channelCpa.length > 0
    ? channelCpa.reduce((a, b) => (a.cpa < b.cpa ? a : b))
    : null;
  const worstChannel = channelCpa.length > 0
    ? channelCpa.reduce((a, b) => (a.cpa > b.cpa ? a : b))
    : null;

  const findings = deriveFindings(cc, targetCpa);
  const channels = cc.channelGroups?.ok ? topChannelsBySessions(cc.channelGroups.rows ?? []) : [];

  const missing: string[] = [];
  if (!ga?.configured) missing.push('Google Analytics');
  if (!gads?.configured) missing.push('Google Ads');
  if (metaAdsEnabled && !meta?.configured) missing.push('Meta Ads');

  const targets: { metric: string; target: number; current: number; unit: string; isHigherBetter: boolean }[] = [
    { metric: 'Monthly conversions', target: 100, current: totalConv, unit: '', isHigherBetter: true },
    { metric: 'Blended CPA', target: targetCpa, current: blendedCpa || 0, unit: '$', isHigherBetter: false },
    { metric: 'Organic sessions', target: 5000, current: cc.organic?.sessions ?? 0, unit: '', isHigherBetter: true },
    { metric: 'Paid spend / mo', target: 25000, current: totalSpend, unit: '$', isHigherBetter: false },
  ];

  return (
    <div className="space-y-6 p-6">
      {missing.length > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span><strong>{missing.join(', ')}</strong> not connected — KPIs will be incomplete until you wire up the missing providers.</span>
          <Link to="/admin/marketing-integrations" className="ml-auto">
            <Button variant="ghost" size="sm"><Plug className="h-4 w-4 mr-1" />Manage</Button>
          </Link>
        </div>
      )}

      {/* ── 5 KPI cards ───────────────────────────────────────────── */}
      <MetricCardGrid columns={5}>
        <StatCard
          label="Total Spend"
          value={totalSpend > 0 ? formatMoney(totalSpend) : '—'}
          subtitle={`${periodLabel(cc.days).replace(/^l/, 'L')} · all paid`}
          tone="blue"
          icon={<DollarSign className="h-4 w-4" />}
        />
        <StatCard
          label="Total Conversions"
          value={totalConv > 0 ? formatNumber(Math.round(totalConv)) : '—'}
          subtitle="GA4 + paid combined"
          tone="green"
          icon={<Target className="h-4 w-4" />}
        />
        <StatCard
          label="Blended CPA"
          value={blendedCpa > 0 ? formatMoney(blendedCpa) : '—'}
          subtitle={`Target ${formatMoney(targetCpa)}`}
          tone={blendedCpa > 0 && blendedCpa <= targetCpa ? 'green' : 'red'}
          icon={<TrendingDown className="h-4 w-4" />}
        />
        <StatCard
          label="Best Channel CPA"
          value={bestChannel ? formatMoney(bestChannel.cpa) : '—'}
          subtitle={bestChannel?.channel ?? '—'}
          tone="emerald"
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <StatCard
          label="Worst Channel CPA"
          value={worstChannel ? formatMoney(worstChannel.cpa) : '—'}
          subtitle={worstChannel?.channel ?? '—'}
          tone="amber"
          icon={<TrendingDown className="h-4 w-4" />}
        />
      </MetricCardGrid>

      {/* ── Findings cards ───────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
          Highest-leverage findings
        </h2>
        {findings.length === 0 ? (
          <Card><CardBody>
            <div className="text-sm text-slate-500 py-4 text-center">
              Connect more providers and check back — we surface findings once there's enough live data.
            </div>
          </CardBody></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {findings.map((f, i) => {
              const s = TONE_STYLES[f.tone];
              return (
                <div key={i} className={cn('rounded-lg border p-4', s.card)}>
                  <div className="flex items-center gap-2 mb-2">
                    <s.Icon className={cn('h-4 w-4', s.icon)} />
                    <Badge variant={f.tone === 'good' ? 'success' : f.tone === 'warn' ? 'warning' : 'error'} size="sm">
                      {f.metric}
                    </Badge>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mb-1">{f.title}</div>
                  <div className="text-xs text-slate-700 leading-relaxed">{f.body}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── 3 Strategic pillars ───────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
          Three strategic pillars
        </h2>
        <div className={`grid grid-cols-1 gap-4 ${SEARCH_CONSOLE_VISIBLE ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          <Pillar
            num="01"
            title="Stop the bleed"
            body={
              totalSpend > 0
                ? `Audit every campaign over ${formatMoney(targetCpa * 3)} CPA and pause anything spending $1K+ with under 3 conversions. The Ad Channel Findings tab lists each one.`
                : 'Once paid spend is connected, this pillar becomes "audit and pause underperformers" — driven by live CPA thresholds.'
            }
            tone="bad"
          />
          <Pillar
            num="02"
            title="Convert the traffic you already have"
            body={
              cc.contentPages?.ok
                ? `${(cc.contentPages.rows ?? []).filter((p) => p.sessions >= 500 && p.conversions === 0).length} pages have 500+ sessions and 0 conversions. Add CTAs and capture forms before scaling acquisition.`
                : 'Connect GA4 to surface high-traffic pages with broken funnels.'
            }
            tone="warn"
          />
          {SEARCH_CONSOLE_VISIBLE && (
            <Pillar
              num="03"
              title="Compound organic"
              body={
                cc.gsc?.ok
                  ? `${(cc.gsc.topQueries ?? []).filter((q) => q.position > 10 && q.position <= 20 && q.impressions >= 100).length} keywords sit at the bottom of page 1 / top of page 2. One round of refresh content should claim them.`
                  : 'Connect Search Console to surface page-2 quick-wins ready for content refresh.'
              }
              tone="good"
            />
          )}
        </div>
      </section>

      {/* ── Traffic by category + Growth targets ──────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              Traffic by channel · {periodLabel(cc.days)}
            </CardTitle>
          </CardHeader>
          <CardBody>
            {!cc.channelGroups?.ok ? (
              <div className="py-12 text-center text-sm text-slate-500">
                {cc.channelGroups?.error ?? 'Connect GA4 to see channel breakdown.'}
              </div>
            ) : channels.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-500">No traffic data yet.</div>
            ) : (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channels} layout="vertical" margin={{ top: 10, right: 16, left: 30, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} />
                    <YAxis type="category" dataKey="channel" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={130} />
                    <Tooltip />
                    <Bar dataKey="sessions" fill="#4f46e5" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" />
              Growth targets
            </CardTitle>
          </CardHeader>
          <CardBody>
            <table className="w-full text-sm">
              <tbody>
                {targets.map((t) => {
                  const onTrack = t.isHigherBetter ? t.current >= t.target * 0.7 : t.current > 0 && t.current <= t.target * 1.2;
                  const status = t.current === 0 ? 'pending' : onTrack ? 'good' : 'warn';
                  return (
                    <tr key={t.metric} className="border-b border-slate-100 last:border-0">
                      <td className="py-2.5 text-slate-700 font-medium">{t.metric}</td>
                      <td className="py-2.5 text-right text-xs text-slate-500 tabular-nums">
                        target {t.unit === '$' ? formatMoney(t.target) : formatNumber(t.target)}
                      </td>
                      <td className="py-2.5 text-right tabular-nums font-semibold">
                        {t.current === 0 ? '—' : t.unit === '$' ? formatMoney(t.current) : formatNumber(Math.round(t.current))}
                      </td>
                      <td className="py-2.5 pl-3 w-16 text-right">
                        <Badge
                          variant={status === 'good' ? 'success' : status === 'warn' ? 'warning' : 'neutral'}
                          size="sm"
                        >
                          {status === 'good' ? '✓' : status === 'warn' ? '⚠' : '–'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Pillar({ num, title, body, tone }: { num: string; title: string; body: string; tone: 'good' | 'warn' | 'bad' }) {
  const accent = tone === 'good' ? 'border-l-emerald-500' : tone === 'warn' ? 'border-l-amber-500' : 'border-l-red-500';
  return (
    <div className={cn('rounded-lg border border-slate-200 border-l-4 bg-white p-4', accent)}>
      <div className="text-xs font-bold text-slate-400 tracking-wider mb-1">{num}</div>
      <div className="text-base font-semibold text-slate-900 mb-2">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}
